import { BillingConfiguration } from 'models/billing-configuration'
import { ErpTransferStatisticLine } from 'models/erp-transfer-statistic'
import { InvoiceFile, FileType } from 'models/invoice-file'
import { InvoicePeriodStatistic } from 'models/invoice-period-statistic'
import { InvoiceStatistic } from 'models/invoice-statistic'
import { InvoicesSendResult } from 'models/invoices-send-result'
import {
    ACTIVITY_BASED_INVOICE_TYPE,
    ALL_INVOICE_TYPES,
    GROUPING_TYPE_BY_BUILDING,
    GROUPING_TYPE_BY_PRODUCT,
    PERIODIC_INVOICE_TYPE,
    USER_INVOICE_TYPE,
} from 'pages/dashboard-page/dashboard-constants'
import { Observable } from 'rxjs'
import { apiUrl, httpGet, httpGetFile, httpPost } from 'services/httpService'

export default class DashboardClient {
    public static getInvoices(): Observable<InvoiceStatistic> {
        return httpGet<InvoiceStatistic>(apiUrl(`/Dashboard/Invoices`))
    }
    public static getInvoiceFiles(): Observable<InvoiceFile[]> {
        return httpGet<InvoiceFile[]>(apiUrl(`/Dashboard/InvoiceFiles`))
    }
    public static getInvoiceFile(type: FileType, id: string): Promise<any> {
        return httpGetFile(apiUrl(`/Dashboard/DownloadInvoiceFile?type=${type}&id=${id}`))
    }
    public static getConfiguration(): Observable<BillingConfiguration> {
        return httpGet<BillingConfiguration>(apiUrl(`/Dashboard/Configuration`))
    }
    public static getInvoicePeriodStatistics(year: number): Observable<InvoicePeriodStatistic[]> {
        return httpGet<InvoicePeriodStatistic[]>(apiUrl(`/Dashboard/InvoicePeriodStatistics?year=${year}`))
    }
    public static sendPeriodToErp(
        year: number,
        period: number,
        invoiceType: number,
        periodicCostType: number,
        groupingType: number,
        limit?: number,
    ): Observable<InvoicesSendResult> {
        return httpPost<InvoicesSendResult>(apiUrl('/Dashboard/SendPeriodToErp'), {
            year: year,
            period: period,
            selectedInvoiceTypes:
                invoiceType === ALL_INVOICE_TYPES
                    ? [PERIODIC_INVOICE_TYPE, ACTIVITY_BASED_INVOICE_TYPE, USER_INVOICE_TYPE]
                    : [invoiceType],
            periodicCostType: periodicCostType,
            groupByProductEnabled: groupingType === GROUPING_TYPE_BY_PRODUCT ? true : false,
            limit: limit,
        })
    }

    public static generateDiffFile(
        fromYear: number,
        fromPeriod: number,
        year: number,
        period: number,
        groupingType: number,
    ): Observable<InvoicesSendResult> {
        return httpPost<InvoicesSendResult>(apiUrl('/Dashboard/GenerateDiffFile'), {
            fromYear: fromYear,
            fromPeriod: fromPeriod,
            year: year,
            period: period,
            groupByBuilding: groupingType === GROUPING_TYPE_BY_BUILDING,
        })
    }

    public static refreshPeriodStatistics(year: number, period: number): Observable<InvoicePeriodStatistic> {
        return httpGet<InvoicePeriodStatistic>(
            apiUrl(`/Dashboard/RefreshPeriodStatistics?year=${year}&period=${period}`),
        )
    }
    public static getInvoiceSummary(year: number, period: number, productId: string): Promise<any> {
        return httpGetFile(
            apiUrl(`/Dashboard/GetPeriodInvoiceSummary?year=${year}&period=${period}&productId=${productId}`),
        )
    }

    public static getErrorReport(year: number, period: number): Promise<any> {
        return httpGetFile(apiUrl(`/Dashboard/GenerateErrorReport?year=${year}&period=${period}`))
    }

    public static migrateData(): Observable<void> {
        return httpGet<void>(apiUrl(`/ContractMigration/migrateContracts`))
    }

    public static getErpTransferStatistics(year: number, id?: string): Observable<ErpTransferStatisticLine[]> {
        return httpGet<ErpTransferStatisticLine[]>(
            apiUrl(`/Dashboard/ErpTransferStatistics?year=${year}${id ? `&id=${id}` : ''}`),
        )
    }

    public static setErpTransferStatisticsFinished(
        statistics: ErpTransferStatisticLine,
    ): Observable<ErpTransferStatisticLine> {
        return httpPost<ErpTransferStatisticLine>(apiUrl('/Dashboard/FinishErpTransfer'), statistics)
    }

    public static resetPeriod(year: number, period: number): Observable<any> {
        return httpPost(apiUrl(`/Dashboard/ResetPeriod?year=${year}&period=${period}`), { year, period })
    }

    public static generateInvoices(year: number, period: number): Observable<any> {
        return httpPost(apiUrl(`/Dashboard/GenerateInvoices?year=${year}&period=${period}`), { year, period })
    }
}
